import React, { useEffect } from "react";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { ShieldCheck, FileText, AlertCircle, UserCheck, Lock } from "lucide-react";

const termsSections = [
  {
    icon: <ShieldCheck className="w-6 h-6 text-red-600" />,
    title: "1. Acceptance of Terms",
    content:
      "By accessing or using any part of the Grand Cinemaz platform, including our website, mobile applications, or in-cinema services, you agree to comply with and be legally bound by these Terms of Service. If you do not agree, please refrain from using our services.",
  },
  {
    icon: <FileText className="w-6 h-6 text-red-600" />,
    title: "2. Ticket Booking & Refund Policy",
    content:
      "All ticket purchases are final once confirmed. Refunds are only granted for show cancellations initiated by Grand Cinemaz. Users are advised to verify all booking details before proceeding to payment. Service fees may apply and are non-refundable.",
  },
  {
    icon: <AlertCircle className="w-6 h-6 text-red-600" />,
    title: "3. Code of Conduct",
    content:
      "Patrons are expected to maintain appropriate behavior within cinema premises. Disruptive actions, misuse of property, or violations of community standards may result in denial of entry or removal without refund. Grand Cinemaz reserves the right to report serious misconduct to law enforcement.",
  },
  {
    icon: <Lock className="w-6 h-6 text-red-600" />,
    title: "4. Privacy & Data Security",
    content:
      "We are committed to safeguarding your privacy. Any personal data collected is used strictly for service delivery and customer support. Your data is never sold or shared without consent. Our systems follow stringent security protocols.",
  },
  {
    icon: <FileText className="w-6 h-6 text-red-600" />,
    title: "5. Intellectual Property Rights",
    content:
      "All media, logos, trademarks, branding elements, and website content are the intellectual property of Grand Cinemaz or its licensors. Unauthorized use, duplication, or redistribution is strictly prohibited and may invite legal action.",
  },
  {
    icon: <UserCheck className="w-6 h-6 text-red-600" />,
    title: "6. Eligibility & Account Responsibility",
    content:
      "Users must be at least 13 years old to create an account. You are responsible for maintaining the confidentiality of your login credentials and all activity under your account. Notify our team immediately of any suspected breach.",
  },
  {
    icon: <ShieldCheck className="w-6 h-6 text-red-600" />,
    title: "7. Amendments & Termination",
    content:
      "Grand Cinemaz reserves the right to modify these terms at any time. Changes will be updated on this page and are effective immediately upon posting. Continued usage after changes implies acceptance of the revised terms.",
  },
];

const TermsOfService = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 ">
      {/* Hero Section */}
      <div
        className="relative bg-cover bg-center bg-no-repeat h-[720px]"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/7991486/pexels-photo-7991486.jpeg?auto=compress&cs=tinysrgb&w=600')"
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 px-6 py-28 md:py-40 text-center text-white flex flex-col items-center justify-center h-full">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-extrabold"
          >
            Terms of Service
          </motion.h1>
          <p className="mt-4 max-w-3xl mx-auto text-lg md:text-xl text-gray-200">
            Welcome to Grand Cinemaz, a premium cinema experience by{" "}
            <a
              href="https://dbgroup.dodunsoftsolutions.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-400 underline hover:text-amber-300"
            >
              Dev Bhoomi Group Companies
            </a>. By continuing to use our platform, you agree to the terms outlined below.
          </p>
        </div>
      </div>


      {/* Terms Content */}
      <div className="max-w-6xl mx-auto px-4 md:px-10 py-16 space-y-10">
        {termsSections.map((section, idx) => (
          <motion.div
            key={idx}
            className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 "
            data-aos="fade-up"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">{section.icon}</div>
              <div>
                <h2 className="text-xl font-bold mb-2">{section.title}</h2>
                <p className="text-gray-700 leading-relaxed">{section.content}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default TermsOfService;
